import React, { useState } from "react";
import Select, { StylesConfig } from "react-select";
import { EstimateType } from "../../../shared/common/EstimateEngine/EstimateEngineTypes";
import { CRM_INTEGRATION_FIELDS, CRMIntegration, CRMIntegrationField } from "./crmIntegrationTypes";

export const INPUT_CLASS =
	"w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";

/** Label + optional help text, shared by every field below. */
const FieldShell: React.FC<{ field: CRMIntegrationField; children: React.ReactNode }> = ({ field, children }) => (
	<div>
		<label className="block text-gray-700 text-sm font-medium mb-1">
			{field.label}
			{field.required ? <span className="text-red-500"> *</span> : <span className="text-gray-400"> (optional)</span>}
		</label>
		{field.helpText && <div className="text-gray-500 text-xs mb-1">{field.helpText}</div>}
		{children}
	</div>
);

const CRMTextField: React.FC<{
	field: CRMIntegrationField;
	value: string;
	onChange: (value: string) => void;
}> = ({ field, value, onChange }) => (
	<FieldShell field={field}>
		<input
			type="text"
			value={value}
			onChange={(e) => onChange(e.target.value)}
			className={INPUT_CLASS}
			placeholder={field.required ? "" : "Leave blank to omit"}
		/>
	</FieldShell>
);

const CRMSecretField: React.FC<{
	field: CRMIntegrationField;
	value: string;
	onChange: (value: string) => void;
}> = ({ field, value, onChange }) => {
	const [revealed, setRevealed] = useState(false);

	return (
		<FieldShell field={field}>
			<div className="relative">
				<input
					type={revealed ? "text" : "password"}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					autoComplete="off"
					className={`${INPUT_CLASS} pr-16`}
					placeholder={field.required ? "" : "Leave blank to omit"}
				/>
				<button
					type="button"
					onClick={() => setRevealed((current) => !current)}
					className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-800 text-xs font-medium"
				>
					{revealed ? "Hide" : "Show"}
				</button>
			</div>
		</FieldShell>
	);
};

const CRMBooleanField: React.FC<{
	field: CRMIntegrationField;
	value: boolean;
	onChange: (value: boolean) => void;
}> = ({ field, value, onChange }) => (
	<label className="flex items-center space-x-2 cursor-pointer">
		<input
			type="checkbox"
			checked={value}
			onChange={(e) => onChange(e.target.checked)}
			className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
		/>
		<span className="text-sm text-gray-700">{field.label}</span>
	</label>
);

const CRMNumberField: React.FC<{
	field: CRMIntegrationField;
	value: number;
	onChange: (value: number) => void;
}> = ({ field, value, onChange }) => {
	const [text, setText] = useState(() => (Number.isFinite(value) ? String(value) : ""));

	return (
		<FieldShell field={field}>
			<input
				type="text"
				inputMode="numeric"
				value={text}
				onChange={(e) => {
					const next = e.target.value;
					setText(next);
					const trimmed = next.trim();
					// Number("") is 0, so the emptiness check has to come first.
					onChange(trimmed === "" ? NaN : Number(trimmed));
				}}
				className={INPUT_CLASS}
			/>
		</FieldShell>
	);
};

type EstimateTypeOption = { value: string; label: string };

const selectStyles: StylesConfig<EstimateTypeOption, true> = {
	control: (base) => ({ ...base, minHeight: "42px", borderRadius: "8px", borderColor: "#D1D5DB" }),
};

const CRMEstimateTypesField: React.FC<{
	field: CRMIntegrationField;
	value: EstimateType[];
	onChange: (value: EstimateType[]) => void;
}> = ({ field, value, onChange }) => {
	// Options ride along on the field descriptor, so the valid set has one definition.
	const options = field.options ?? [];
	return (
		<FieldShell field={field}>
			<Select
				isMulti
				options={options}
				styles={selectStyles}
				value={options.filter((option) => (value as string[]).includes(option.value))}
				onChange={(selected) => onChange(selected.map((option) => option.value as EstimateType))}
				placeholder="None — no jobs will be synced"
				className="text-base font-normal font-['Hanken Grotesk']"
			/>
		</FieldShell>
	);
};

const CRMLockedField: React.FC<{ field: CRMIntegrationField; value: string }> = ({ field, value }) => (
	<div>
		<label className="block text-gray-700 text-sm font-medium mb-1">{field.label}</label>
		<div className="px-3 py-2 text-gray-600 bg-gray-50 border border-gray-200 rounded-lg">{value}</div>
		<div className="text-gray-500 text-xs mt-1">Locked — {field.lockedReason}</div>
	</div>
);

const CRMFieldGroup: React.FC<{
	integration: CRMIntegration;
	/** Keys this integration loaded from the server holding, which must not be edited. */
	lockedKeys: readonly string[];
	onChange: (next: CRMIntegration) => void;
}> = ({ integration, lockedKeys, onChange }) => {
	const source: Record<string, unknown> = integration;
	const set = (key: string, value: unknown) => onChange({ ...integration, [key]: value } as CRMIntegration);

	return (
		<>
			{CRM_INTEGRATION_FIELDS[integration.type].map((field) => {
				if (lockedKeys.includes(field.key)) {
					return <CRMLockedField key={field.key} field={field} value={(source[field.key] as string) ?? ""} />;
				}
				if (field.secret) {
					return (
						<CRMSecretField
							key={field.key}
							field={field}
							value={(source[field.key] as string) ?? ""}
							onChange={(value) => set(field.key, value)}
						/>
					);
				}
				switch (field.kind) {
					case "string":
					case "url":
					case "slug":
						return (
							<CRMTextField
								key={field.key}
								field={field}
								value={(source[field.key] as string) ?? ""}
								onChange={(value) => set(field.key, value)}
							/>
						);
					case "boolean":
						return (
							<CRMBooleanField
								key={field.key}
								field={field}
								value={source[field.key] === true}
								onChange={(value) => set(field.key, value)}
							/>
						);
					case "number":
						return (
							<CRMNumberField
								key={field.key}
								field={field}
								value={source[field.key] as number}
								onChange={(value) => set(field.key, value)}
							/>
						);
					case "estimateTypes":
						return (
							<CRMEstimateTypesField
								key={field.key}
								field={field}
								value={(source[field.key] as EstimateType[]) ?? []}
								onChange={(value) => set(field.key, value)}
							/>
						);
				}
			})}
		</>
	);
};

export default CRMFieldGroup;
